import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useSoftphone } from '../store/softphone';
import { softphoneService } from '../softphone/service';
import type { Campaign, Lead } from '@marnic/shared';
import { formatUKForDisplay } from '@marnic/shared';
import { PowerSessionPanel } from './PowerSessionPanel';

interface CampaignDetailResponse extends Campaign {
  assignedAgentIds: string[];
  _count: { leads: number; calls: number };
}

interface StatsResponse {
  campaignId: string;
  dialled: number;
  connected: number;
  voicemail: number;
  qualified: number;
  meetings: number;
  abandoned: number;
  abandonRate: number;
  avgTalkSeconds: number;
}

export function CampaignDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const setLead = useSoftphone((s) => s.setLead);
  const setCampaignContext = useSoftphone((s) => s.setCampaignContext);

  const campaign = useQuery({
    queryKey: ['campaigns', id],
    enabled: !!id,
    queryFn: () => api.get<CampaignDetailResponse>(`/campaigns/${id}`),
  });
  const stats = useQuery({
    queryKey: ['campaigns', id, 'stats'],
    enabled: !!id,
    refetchInterval: 5000,
    queryFn: () => api.get<StatsResponse>(`/campaigns/${id}/stats`),
  });

  const start = useMutation({
    mutationFn: () => api.post(`/campaigns/${id}/start`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns', id] }),
  });
  const pause = useMutation({
    mutationFn: () => api.post(`/campaigns/${id}/pause`, { reason: 'manual' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns', id] }),
  });
  const scrub = useMutation({
    mutationFn: () => api.post<{ total: number; blocked: number; callable: number }>(`/campaigns/${id}/scrub`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns', id] }),
  });

  const dialNext = async (): Promise<void> => {
    const result = await api.post<{
      ok: boolean;
      reason?: string;
      callId?: string;
      campaignLeadId?: string;
      lead?: Lead;
    }>(`/campaigns/${id}/dial-next`);
    if (!result.ok) {
      alert(`Cannot dial: ${result.reason ?? 'unknown'}`);
      return;
    }
    if (result.lead) setLead(result.lead);
    setCampaignContext({ campaignId: id!, campaignLeadId: result.campaignLeadId });
  };

  const previewNext = async (): Promise<void> => {
    const result = await api.post<{ ok: boolean; lead: Lead | null; campaignLead: { id: string } | null }>(`/campaigns/${id}/next-lead`);
    if (result.lead) {
      setLead(result.lead);
      if (result.campaignLead) setCampaignContext({ campaignId: id!, campaignLeadId: result.campaignLead.id });
    }
  };

  if (!campaign.data) {
    return <p className="text-sm text-ink-500">Loading…</p>;
  }
  const c = campaign.data;
  const s = stats.data;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{c.name}</h1>
          <p className="text-sm text-ink-500 capitalize">
            {c.diallerMode} · {c.linesPerAgent} lines / agent · {c.status}
            {c.pauseReason ? ` (${c.pauseReason})` : ''}
          </p>
          {c.description && <p className="mt-2 text-sm text-ink-700 max-w-2xl">{c.description}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          {c.status === 'active' ? (
            <button type="button" className="btn-secondary" onClick={() => pause.mutate()}>⏸ Pause</button>
          ) : (
            <button type="button" className="btn-primary" onClick={() => start.mutate()}>▶ Start</button>
          )}
          <button type="button" className="btn-secondary" onClick={() => scrub.mutate()} disabled={scrub.isPending}>
            {scrub.isPending ? 'Scrubbing…' : '🧹 Compliance scrub'}
          </button>
          {c.diallerMode === 'preview' ? (
            <button type="button" className="btn-primary" onClick={() => void previewNext()}>👁 Preview next</button>
          ) : c.diallerMode === 'power' ? null : (
            <button type="button" className="btn-primary" onClick={() => void dialNext()}>📞 Dial next</button>
          )}
        </div>
      </header>

      {c.diallerMode === 'power' && <PowerSessionPanel campaignId={id!} linesPerAgent={c.linesPerAgent} campaignStatus={c.status} />}

      <section className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
        <Stat label="Dialled" value={s?.dialled ?? 0} />
        <Stat label="Connected" value={s?.connected ?? 0} />
        <Stat label="Voicemail" value={s?.voicemail ?? 0} />
        <Stat label="Qualified" value={s?.qualified ?? 0} />
        <Stat label="Abandoned" value={s?.abandoned ?? 0} />
        <Stat label="Abandon rate" value={`${((s?.abandonRate ?? 0) * 100).toFixed(2)}%`} highlight={s ? s.abandonRate > c.abandonedCallThreshold : false} />
        <Stat label="Avg talk" value={`${Math.round(s?.avgTalkSeconds ?? 0)}s`} />
      </section>

      {scrub.data && (
        <div className="card p-4 text-sm">
          <h3 className="font-semibold mb-1">Scrub results</h3>
          <p>Total {scrub.data.total} · Callable {scrub.data.callable} · Blocked {scrub.data.blocked}</p>
        </div>
      )}

      <CampaignLeads campaignId={id!} />
    </div>
  );
}

interface CampaignLeadRow {
  id: string;
  status: string;
  attemptCount: number;
  nextAttemptAt: string | null;
  lastDisposition: string | null;
  lead: Lead;
}

function CampaignLeads({ campaignId }: { campaignId: string }): JSX.Element {
  const setLead = useSoftphone((s) => s.setLead);
  const setCampaignContext = useSoftphone((s) => s.setCampaignContext);
  const leads = useQuery({
    queryKey: ['campaigns', campaignId, 'leads'],
    queryFn: () => api.get<{ total: number; items: CampaignLeadRow[] }>(`/campaigns/${campaignId}/leads?limit=100`),
  });

  const callLead = async (lead: Lead, campaignLeadId: string): Promise<void> => {
    setLead(lead);
    setCampaignContext({ campaignId, campaignLeadId });
    await softphoneService.dial({ toRaw: lead.phoneE164, leadId: lead.id, campaignId });
  };

  return (
    <div className="card overflow-hidden">
      <header className="border-b border-ink-200 px-4 py-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-500">Lead queue</h2>
        <span className="text-xs text-ink-500">{leads.data?.total ?? 0} entries</span>
      </header>
      <div className="overflow-x-auto">
      <table className="w-full text-sm whitespace-nowrap">
        <thead className="bg-ink-50 text-left text-xs uppercase tracking-wider text-ink-500">
          <tr>
            <th className="px-4 py-2">Name</th>
            <th className="px-4 py-2">Company</th>
            <th className="px-4 py-2">Phone</th>
            <th className="px-4 py-2">Status</th>
            <th className="px-4 py-2">Attempts</th>
            <th className="px-4 py-2 w-28">Action</th>
          </tr>
        </thead>
        <tbody>
          {leads.data?.items.map((row) => (
            <tr key={row.id} className="border-t border-ink-100">
              <td className="px-4 py-2">{row.lead.firstName} {row.lead.lastName}</td>
              <td className="px-4 py-2">{row.lead.company ?? '–'}</td>
              <td className="px-4 py-2 font-mono">{formatUKForDisplay(row.lead.phoneE164)}</td>
              <td className="px-4 py-2">
                <span className="badge bg-ink-100 text-ink-700">{row.status.replace(/_/g, ' ')}</span>
              </td>
              <td className="px-4 py-2 tabular-nums">{row.attemptCount}</td>
              <td className="px-4 py-2">
                <button
                  type="button"
                  className="btn-primary text-xs"
                  disabled={['tps_blocked', 'ctps_blocked', 'dnc', 'invalid_number'].includes(row.status)}
                  onClick={() => void callLead(row.lead, row.id)}
                >
                  📞 Call
                </button>
              </td>
            </tr>
          ))}
          {leads.data?.items.length === 0 && (
            <tr><td colSpan={6} className="px-4 py-6 text-center text-ink-500">No leads queued.</td></tr>
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string | number; highlight?: boolean }): JSX.Element {
  return (
    <div className={`card p-3 ${highlight ? 'border-red-300 bg-red-50' : ''}`}>
      <div className="text-xs uppercase tracking-wide text-ink-500">{label}</div>
      <div className={`mt-1 text-xl font-semibold tabular-nums ${highlight ? 'text-red-700' : ''}`}>{value}</div>
    </div>
  );
}
